/**
 * I18nProvider + useT — Sprint BK.
 *
 * Tiny i18n: a React context that holds the active language, a
 * `useT()` hook that returns a translator function, and a
 * persistence layer (localStorage) so the user's pick survives
 * a refresh.
 *
 * Why hand-rolled rather than react-intl / i18next:
 *   - The dictionaries are small enough (a few hundred keys) that
 *     a full library is overkill.
 *   - We want zero ICU / pluralisation cleverness today —
 *     parameter interpolation is a `{token}` find-and-replace.
 *   - Bundle stays slim. The whole module + dictionaries is
 *     ~6 KB gzipped.
 *
 * Adding pluralisation later is a one-line change: switch `t` to
 * delegate to react-intl's `formatMessage` for keys that contain
 * `{count, plural, ...}`. The call-site API doesn't need to
 * change.
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from "react";

import { DICTIONARIES, LANGS, type Lang } from "./messages";


const STORAGE_KEY = "vseh.admin.lang";


function detectInitialLang(): Lang {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "ru" || saved === "en") return saved;
  } catch { /* localStorage blocked — fall through */ }
  // Browser preference. Anything that isn't en-* falls back to ru
  // — the dominant audience.
  const browser = (
    typeof navigator !== "undefined" ? navigator.language : ""
  ).toLowerCase();
  return browser.startsWith("en") ? "en" : "ru";
}


interface I18nContextValue {
  lang:    Lang;
  setLang: (l: Lang) => void;
  /** Translate a key. Returns a `[missing: key]` placeholder when
   *  the key is absent from the active dictionary — invisible
   *  bugs are worse than visible ones. */
  t:       (key: string, params?: Record<string, string | number>) => string;
}


const Ctx = createContext<I18nContextValue | null>(null);


export function I18nProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [lang, setLangState] = useState<Lang>(detectInitialLang);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, lang); } catch { /* ignore */ }
    // Set `<html lang>` so Speech Synthesis / a11y trees pick up
    // the change. Most UA conventions read this attribute.
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const setLang = useCallback((l: Lang) => setLangState(l), []);

  const t = useCallback(
    (key: string, params?: Record<string, string | number>) => {
      const dict = DICTIONARIES[lang];
      const tpl  = dict[key] ?? `[missing: ${key}]`;
      if (!params) return tpl;
      return Object.entries(params).reduce(
        (acc, [k, v]) => acc.replaceAll(`{${k}}`, String(v)),
        tpl,
      );
    },
    [lang],
  );

  const value = useMemo<I18nContextValue>(
    () => ({ lang, setLang, t }),
    [lang, setLang, t],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}


export function useT(): I18nContextValue["t"] {
  const ctx = useContext(Ctx);
  if (ctx === null) {
    throw new Error("useT must be used inside <I18nProvider>");
  }
  return ctx.t;
}


export function useLang(): { lang: Lang; setLang: (l: Lang) => void } {
  const ctx = useContext(Ctx);
  if (ctx === null) {
    throw new Error("useLang must be used inside <I18nProvider>");
  }
  return { lang: ctx.lang, setLang: ctx.setLang };
}


/**
 * Header dropdown. Rendered once in App.tsx near the user menu.
 */
export function LangSwitcher(): JSX.Element {
  const { lang, setLang } = useLang();
  return (
    <select
      aria-label="Language"
      value={lang}
      onChange={(e) => setLang(e.target.value as Lang)}
      className="rounded-sm border border-base-200 bg-surface px-2 py-1 text-xs"
    >
      {LANGS.map((l) => (
        <option key={l.code} value={l.code}>{l.label}</option>
      ))}
    </select>
  );
}
